import React, { useState } from 'react';

import './Program.users.scss';
import {Subject} from "rxjs";
import {useQuery} from "@apollo/client";
import gqlSchemaLibrary from "../../services/graphql.schemas";
import ButtonIcon from "../../forms/buttons/Button.icon";
import SpinnerLoader from "../../visuals/SpinnerLoader";
import Checkbox from "../../forms/inputs/Checkboxk";
import ProgramUserItem from "./Program.user.item";
import ItemDeleteDialog from "../dialogs/Team.delete.dialog";
import ProgramMembersDialog from "../dialogs/Program.members.dialog";
import ItemMenu from "../../forms/menu/MenuComponent";
import {clearGqlTypename} from "../../utils/clearTypename";
import {useTranslation} from "react-i18next";
import {PageViewLog} from "../../services/interactions/page.view";
import AuditTrail, {PAGES} from "../../services/interactions/audit.trail";
import {useParams} from "react-router-dom";

function ProgramUsers({currentOrg, currentUser}) {
    let {programId} = useParams();
    const {t} = useTranslation(['common','teams','users']);
    const [sortAscending, setSortAscending] = useState(true);
    const [sorting, setSorting] = useState('name');

    const [searchFocused, changeSearchFocused] = useState(false);
    const [search, changeSearch] = useState('');
    const [searchTerm] = useState(new Subject());

    const [program, setProgram] = useState(null);
    const [loadData, setLoadData] = useState(true);
    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [hoverUser, setHoverUser] = useState(null);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [openMembersModal, setOpenMembersModal] = useState(false);
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [selectAll, setSelectAll] = useState(false);


    useQuery(gqlSchemaLibrary.programs.query.getProgramById, {
        variables: {
            id: programId
        },
        onCompleted: ({ getProgramById }) => {
            setProgram(clearGqlTypename(getProgramById));
            setUsers(clearGqlTypename(getProgramById.users));
            setLoadData(false);
        },
        fetchPolicy: 'cache-and-network'
    })

    const handleFocus = (bool) => {
        return () => {
            AuditTrail.sendLog(PAGES.PROGRAM_USERS, {tag: 'input', event: 'onSearch', action: 'input search users', focus: bool, value: search});
            changeSearchFocused(bool);
        }
    }

    const handleSearch = (e) => {
        const value = e.target.value;
        changeSearch(value)
        searchTerm.next(value);
    }

    const sortList = (key) => {
        return () => {
            AuditTrail.sendLog(PAGES.PROGRAM_USERS, {tag: 'button', event: 'onClick', action: 'button toggle sort', sort: key, sort_ascending: !sortAscending});
            setSorting(key);
            setSortAscending(!sortAscending);
        }
    }

    const onChange = (user) => {
        const _team = selectedUsers.find((t) => t._id === user._id);
        const index = selectedUsers.indexOf(_team);
        const checked = index > -1;
        AuditTrail.sendLog(PAGES.PROGRAM_USERS, {tag: 'checkbox', event: 'onCheck', action: 'button selected user', program_id: program._id, user_id: user._id, check: !checked});
        if (checked) {
            selectedUsers.splice(index, 1);
        } else {
            selectedUsers.push(user);
        }
        if (!selectedUsers.length) {
            setSelectAll(false);
        }
        setSelectedUsers(Object.assign([], selectedUsers));
    }

    const onDeleteUsers = () => {
        if (selectedUsers.length) {
            setOpenDeleteModal(true);
        }
    }

    const onSelectAll = () => {
        const status = !selectAll;
        if (status) {
            setSelectedUsers(Object.assign([], users));
        } else {
            setSelectedUsers([]);
        }
        AuditTrail.sendLog(PAGES.PROGRAM_USERS, {tag: 'checkbox', event: 'onCheck', action: 'button selected all users', program_id: program._id, check: status});
        setSelectAll(status);
    }

    const onOpenMembersModal = () => {
        setOpenMembersModal(true);
    }

    const onCloseModal = (program, users, teams) => {
        if (program && users) {
            program.users = users;
            setUsers(users)
            setProgram(program);
        }
        setOpenMembersModal(false);
    }

    const onOpenDeleteModal = (user) => {
        setSelectedUser(user);
        setOpenDeleteModal(true);
    }

    const closeDeleteModal = (_items) => {
        if (_items) {
            for (const _u of _items) {
                const _user = users.find((u) => u._id === _u._id);
                const index = users.indexOf(_user);
                if (index > -1) {
                    users.splice(index, 1);
                }
            }
            setUsers(users);
        }
        setSelectedUser(null);
        setOpenDeleteModal(false);
    }

    const filteredUsers= users.filter(p => {
        if (!search) {
            return true;
        }
        const firstName = (p.user.bio.firstname || '').toLowerCase();
        const lastName = (p.user.bio.lastname || '').toLowerCase();
        const searchWords = search.split(' ').map(w => w.toLowerCase());
        return searchWords.reduce((p, word) => {
            return p && (firstName.includes(word) || lastName.includes(word));
            }, true);
        })
        .sort( (a, b) => {
            if(!sorting){
                return true;
            }
            if(sorting === 'name'){
                const aName = (a.user.bio.lastname||'')+' '+(a.user.bio.firstname||'');
                const bName = (b.user.bio.lastname||'')+' '+(b.user.bio.firstname||'');
                if(sortAscending){
                    return aName.localeCompare(bName);
                }else{
                    return bName.localeCompare(aName);
                }
            }else{
                const aRole = a.role;
                const bRole = b.role;
                if(sortAscending){
                    return aRole.localeCompare(bRole);
                }else{
                    return bRole.localeCompare(aRole);
                }
            }
        })

    const onBack = () => {
        AuditTrail.sendLog(PAGES.PROGRAM_USERS, {tag: 'button', event: 'onClick', action: 'button cancel program users'});
        window.history.back();
    }

    return (
        <div id="UsersProgram">
            <div className="filter">
                <div className="programOptions">
                    <i className="material-icons" onClick={onBack}>
                        arrow_back_ios
                    </i>
                    <div className="progTitle">
                        <h3>{!!program && program.name ? program.name : ''}</h3>
                        <div className="_active">
                            {t('active')}
                    </div>
                    </div>
                </div>
                <div className={`programFilters ${currentUser.userRoles.userPatients.edit ? '' : '_col3'}`}>
                    <div className="searchFilters">
                        <div></div>
                        <div className="searchContainer">
                            <div className={`search ${searchFocused ? 'focused' : ''}`}>
                                <input value={search} placeholder={t('search')} onChange={handleSearch} onFocus={handleFocus(true)} onBlur={handleFocus(false)} />
                                <i className="material-icons">
                                    search
                            </i>
                            </div>
                        </div>
                    </div>
                    <p className="_counter">
                        {filteredUsers.length} user{filteredUsers.length === 1? '' : 's'}
                    </p>
                    {
                        currentUser.userRoles.userPatients.edit?
                            <div className="enrollAction" onClick={onOpenMembersModal}>
                                <ButtonIcon>
                                    <span className="material-icons">
                                        add
                                </span>
                                </ButtonIcon>
                                <p>{t('teams:add-user')}</p>
                            </div> : <div className="empty"></div>
                    }
                </div>
            </div>
            <div className="usersList scrollBar">
                <div className="headers">
                    <div>
                        {
                            currentUser.userRoles.userPatients.edit? <Checkbox bool={selectAll} onChange={onSelectAll}/> : ''
                        }
                    </div>
                    <div></div>
                    <div className="selectable sortable">
                        <p onClick={sortList('name')}>
                            {t('name')}
                        </p>
                        <div className="_updown" onClick={sortList('name')}>
                            <span className="_up material-icons">expand_less</span>
                            <span className="_down material-icons">expand_more</span>
                        </div>
                    </div>
                    <div className="selectable">
                        <p>
                            {t('users:telephone')}
                        </p>
                        <div className="_updown"></div>
                    </div>
                    <div className="selectable sortable">
                        <p onClick={sortList('role')}>
                            {t('users:role')}
                        </p>
                        <div className="_updown" onClick={sortList('role')}>
                            <span className="_up material-icons">expand_less</span>
                            <span className="_down material-icons">expand_more</span>
                        </div>
                    </div>
                    <div className="selectable">
                        <p>
                            {t('users:date-of-birth')}
                        </p>
                        <div className="_updown"></div>
                    </div>

                    <div>
                        {
                            currentUser.userRoles.userPatients.edit && selectedUsers.length?
                                <ItemMenu actionWillRedirect={false} fixed={true}>
                                    <>
                                        <div className="_option" onClick={onDeleteUsers} >
                                            <p>{t('teams:remove-users')}</p>
                                        </div>
                                    </>
                                </ItemMenu> : ''
                        }
                    </div>
                </div>
                {
                    filteredUsers.map((clinician) => (
                        <ProgramUserItem key={clinician._id} currentUser={currentUser} currentOrg={currentOrg} clinician={clinician} selectedUsers={selectedUsers} hoverUser={hoverUser} onHover={setHoverUser} onDeleteItem={onOpenDeleteModal} onChange={onChange} />
                    ))
                }
                {loadData ? <SpinnerLoader /> : ''}
            </div>
            {
               <ItemDeleteDialog type={'user'} program={program} item={selectedUser} items={selectedUsers} openModal={openDeleteModal} closeModal={closeDeleteModal} />
            }
            {
                program? <ProgramMembersDialog type={'user'} program={program} openModal={openMembersModal} closeModal={onCloseModal} /> : ''
            }
        </div>
    );
}

export default PageViewLog(ProgramUsers);
